import { gql, useQuery } from "@apollo/client";
import { ForbiddenError } from "../components/ErrorBoundary";
import {
  StructParams,
  StructureBase,
  getChildType,
  QueryStructParams,
  VDCounts,
} from "../lib/structures";
import { useEffect } from "react";

export interface IssueCount {
  theme: string;
  issue: string;
  count: number;
}

interface IssueData {
  issuecounts: IssueCount[];
  phase1: {
    current: number;
  };
}

export interface StructureWithIssueCounts extends StructureWithIssueCountsBase {
  microtargeting: IssueData;
  parents: StructureBase[];
  children: StructureWithIssueCountsBase[];
}

interface StructureReturns {
  stats: StructureWithIssueCounts | undefined;
  loading: boolean;
}

export interface StructureWithIssueCountsBase extends StructureBase {
  microtargeting: IssueData;
  counts: VDCounts;
}

interface IssueCountsResult {
  structure: StructureWithIssueCounts;
}

const query = gql`
  query getIssueCounts(
    $type: String!
    $code: String!
    $childType: String!
    $filterLabel: String
  ) {
    structure(type: $type, code: $code, filterLabel: $filterLabel) {
      type
      code
      name
      responsiblePersons {
        firstname
        surname
        displayname
      }
      userHasAccess
      structureHasAssignedWards
      counts {
        total
        filtered
      }
      microtargeting {
        phase1 {
          current
        }
        issuecounts {
          issue
          count
          theme
        }
      }
      parents {
        type
        code
        name
        userHasAccess
      }
      children(type: $childType) {
        type
        code
        name
        responsiblePersons {
          firstname
          surname
          displayname
        }
        userHasAccess
        structureHasAssignedWards
        counts {
          total
          filtered
        }
        microtargeting {
          phase1 {
            current
          }
          issuecounts {
            issue
            count
            theme
          }
        }
      }
    }
  }
`;

export function useIssueCounts({
  type,
  code,
  filterLabel,
}: StructParams): StructureReturns {
  const childType = getChildType({ type, code });
  const { error, data, refetch, loading } = useQuery<
    IssueCountsResult,
    QueryStructParams
  >(query, {
    variables: { type, code, childType, filterLabel },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    refetch();
  }, [filterLabel, refetch]);

  if (error?.graphQLErrors[0]?.extensions?.code === "FORBIDDEN") {
    throw new ForbiddenError("this structure");
  }
  if (error != null) throw error;

  return { stats: data?.structure, loading: loading };
}
